import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import YouTube from 'react-youtube';
import { Image as ImageIcon, X, Video as VideoIcon, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHOTO_CATEGORIES = [
  { id: 'all', label: 'Toutes' },
  { id: 'ilot', label: 'Îlot de Bushumba' },
  { id: 'activites', label: 'Activités' },
  { id: 'formations', label: 'Formations FPT' },
  { id: 'agriculture', label: 'Agri-élevage' },
  { id: 'communaute', label: 'Communauté' },
];

const VIDEO_CATEGORIES = [
  { id: 'all', label: 'Toutes' },
  { id: 'temoignage', label: 'Témoignages' },
  { id: 'activite', label: 'Activités' },
  { id: 'formation', label: 'Formations FPT' },
  { id: 'evenement', label: 'Événements' },
];

const VIDEO_CAT_LABEL = {
  temoignage: 'Témoignage',
  activite: 'Activité',
  formation: 'Formation',
  evenement: 'Événement',
  autre: 'Autre',
};

const YOUTUBE_OPTS = { width: '100%', height: '100%', playerVars: { autoplay: 1, mute: 1, rel: 0 } };
const YOUTUBE_ID_REGEX = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;

const extractYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(YOUTUBE_ID_REGEX);
  return (m && m[2].length === 11) ? m[2] : null;
};

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function GalleryPage() {
  const [tab, setTab] = useState('photos');
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoCat, setPhotoCat] = useState('all');
  const [videoCat, setVideoCat] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [p, v] = await Promise.all([axios.get(`${API}/photos`), axios.get(`${API}/videos`)]);
      setPhotos(p.data);
      setVideos(v.data);
      if (v.data.length > 0) setSelectedVideo(v.data[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredPhotos = useMemo(() => {
    if (photoCat === 'all') return photos;
    return photos.filter((p) => p.category === photoCat);
  }, [photos, photoCat]);

  const filteredVideos = useMemo(() => {
    if (videoCat === 'all') return videos;
    return videos.filter((v) => (v.category || 'activite') === videoCat);
  }, [videos, videoCat]);

  useEffect(() => {
    if (filteredVideos.length === 0) return;
    if (!selectedVideo || !filteredVideos.some((v) => v.video_id === selectedVideo.video_id)) {
      setSelectedVideo(filteredVideos[0]);
    }
  }, [filteredVideos, selectedVideo]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="gallery-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#4A6B48]/10 mb-4">
              <ImageIcon className="text-[#4A6B48]" size={40} />
            </div>
            <h1
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="gallery-title"
            >
              Galerie médias
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Plongez au cœur de nos îlots de développement intégral : photos et vidéos des activités,
              formations FPT et moments de vie communautaire au Sud-Kivu.
            </p>
          </div>

          {/* Tabs Photos / Vidéos */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-lg border-2 border-[#E5E3DB] p-1 bg-white">
              <button
                onClick={() => setTab('photos')}
                data-testid="gallery-tab-photos"
                className={`px-6 py-2 rounded-md text-sm font-bold transition-all inline-flex items-center gap-2 ${
                  tab === 'photos' ? 'bg-[#4A6B48] text-white' : 'text-[#5C665A] hover:text-[#4A6B48]'
                }`}
              >
                <ImageIcon size={16} /> Photos ({photos.length})
              </button>
              <button
                onClick={() => setTab('videos')}
                data-testid="gallery-tab-videos"
                className={`px-6 py-2 rounded-md text-sm font-bold transition-all inline-flex items-center gap-2 ${
                  tab === 'videos' ? 'bg-[#4A6B48] text-white' : 'text-[#5C665A] hover:text-[#4A6B48]'
                }`}
              >
                <VideoIcon size={16} /> Vidéos ({videos.length})
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}

          {!loading && tab === 'photos' && (
            <>
              <div className="flex flex-wrap justify-center gap-2 mb-8">
                {PHOTO_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setPhotoCat(c.id)}
                    data-testid={`gallery-cat-${c.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
                      photoCat === c.id ? 'bg-[#4A6B48] border-[#4A6B48] text-white' : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48] hover:text-[#4A6B48]'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {filteredPhotos.length === 0 ? (
                <div className="text-center py-12" data-testid="no-photos-message">
                  <p className="text-[#5C665A]">Aucune photo disponible pour cette catégorie.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredPhotos.map((photo) => (
                    <button
                      key={photo.photo_id}
                      onClick={() => setLightbox(photo)}
                      data-testid={`photo-thumb-${photo.photo_id}`}
                      className="group relative overflow-hidden rounded-lg aspect-square bg-[#F9F8F6] hover:shadow-xl transition-all"
                    >
                      <img
                        src={resolveImageUrl(photo.image_url)}
                        alt={photo.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                        <p className="text-white text-sm font-bold line-clamp-2">{photo.title}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {!loading && tab === 'videos' && (
            <>
              <div className="flex flex-wrap justify-center gap-2 mb-8">
                {VIDEO_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setVideoCat(c.id)}
                    data-testid={`video-cat-${c.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
                      videoCat === c.id ? 'bg-[#4A6B48] border-[#4A6B48] text-white' : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48] hover:text-[#4A6B48]'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {filteredVideos.length === 0 ? (
                <div className="text-center py-12" data-testid="no-videos-message">
                  <p className="text-[#5C665A]">Aucune vidéo disponible dans cette catégorie.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Player */}
                  <div className="lg:col-span-2">
                    <div className="aspect-video bg-[#F9F8F6] rounded-lg overflow-hidden" data-testid="featured-video">
                      {selectedVideo && selectedVideo.youtube_url && extractYouTubeId(selectedVideo.youtube_url) && (
                        <YouTube
                          key={selectedVideo.video_id}
                          videoId={extractYouTubeId(selectedVideo.youtube_url)}
                          opts={YOUTUBE_OPTS}
                          className="w-full h-full"
                          iframeClassName="w-full h-full"
                        />
                      )}
                      {selectedVideo && !selectedVideo.youtube_url && selectedVideo.external_url && (
                        <video
                          key={selectedVideo.video_id}
                          autoPlay
                          muted
                          controls
                          playsInline
                          preload="metadata"
                          controlsList="nodownload noremoteplayback"
                          onContextMenu={(e) => e.preventDefault()}
                          className="w-full h-full"
                          src={selectedVideo.external_url}
                        />
                      )}
                    </div>
                    {selectedVideo && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {selectedVideo.category && (
                            <span className="text-xs font-bold px-2 py-1 rounded bg-[#4A6B48]/10 text-[#4A6B48]">
                              {VIDEO_CAT_LABEL[selectedVideo.category] || 'Activité'}
                            </span>
                          )}
                        </div>
                        <h2 className="text-2xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          {selectedVideo.title}
                        </h2>
                        <p className="text-sm text-[#5C665A] leading-relaxed">{selectedVideo.description}</p>
                      </div>
                    )}
                  </div>
                  {/* List */}
                  <div className="space-y-3 max-h-[500px] overflow-y-auto" data-testid="video-list">
                    {filteredVideos.map((v) => (
                      <button
                        key={v.video_id}
                        onClick={() => setSelectedVideo(v)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          selectedVideo && selectedVideo.video_id === v.video_id
                            ? 'border-[#4A6B48] bg-[#4A6B48]/5'
                            : 'border-[#E5E3DB] hover:border-[#4A6B48]'
                        }`}
                      >
                        <div className="flex gap-3 items-start">
                          <div className="w-12 h-12 rounded bg-[#4A6B48]/10 flex items-center justify-center shrink-0">
                            <Play className="text-[#4A6B48]" size={18} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-[#2E332D] line-clamp-1">{v.title}</p>
                            <p className="text-xs text-[#5C665A] line-clamp-1">{v.description}</p>
                            {v.category && (
                              <span className="text-xs font-bold text-[#4A6B48] mt-1 inline-block">
                                {VIDEO_CAT_LABEL[v.category] || 'Activité'}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          style={{ zIndex: 9999 }}
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-black/60" style={{ zIndex: 10000 }}>
            <X size={28} />
          </button>
          <div className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={resolveImageUrl(lightbox.image_url)} alt={lightbox.title} className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl" />
            <div className="text-center mt-4 max-w-2xl">
              <h2 className="text-white text-xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{lightbox.title}</h2>
              {lightbox.caption && <p className="text-white/80 text-sm mt-2">{lightbox.caption}</p>}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
